import React, { useEffect, useState, useMemo } from "react";
import {
  Row,
  Col,
  Card,
  DatePicker,
  Button,
  Table,
  Radio,
  Space,
  message,
  Spin,
  Tag,
  Modal,
  Input,
  Empty,
  Tabs,
} from "antd";
import {
  UserOutlined,
  TeamOutlined,
  TrophyOutlined,
  DollarOutlined,
  CalendarOutlined,
  RiseOutlined,
  AppstoreOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { changePageTitle } from "../../utils/title";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Area,
  AreaChart,
} from "recharts";

import { fetchDashboardData } from "./utils/dashboardFetch";
import "./DashboardStyles.css";

const { RangePicker } = DatePicker;
const pieColors = ["#1890ff", "#13c2c2", "#52c41a", "#722ed1"];
const gradientColors = [
  { start: "#1890ff", end: "#1890ff" },
  { start: "#13c2c2", end: "#13c2c2" },
  { start: "#52c41a", end: "#52c41a" },
  { start: "#722ed1", end: "#722ed1" },
];

/**
 * Преобразует данные менеджеров из формата бекенда
 * в простой объект { manager, agreements, kp }
 */
function transformManagers(rawManagers) {
  return rawManagers.map((item) => {
    let agreementsCount = 0;
    let kpCount = 0;
    if (item.documents && Array.isArray(item.documents)) {
      item.documents.forEach((doc) => {
        if (doc.doc_type === "agreement") {
          agreementsCount = doc.count;
        } else if (doc.doc_type === "commercial-proposal") {
          kpCount = doc.count;
        }
      });
    }
    return {
      manager: item.managerName,
      agreements: agreementsCount,
      kp: kpCount,
    };
  });
}

export default function Dashboard() {
  useEffect(() => {
    changePageTitle("Dashboard");
  }, []);

  // Период для фильтрации
  const [dateRange, setDateRange] = useState([
    moment().subtract(7, "days"),
    moment(),
  ]);

  // Лоадер
  const [loading, setLoading] = useState(false);

  // Данные для графиков
  const [requestsData, setRequestsData] = useState([]);      // клиенты по дням
  const [managerData, setManagerData] = useState([]);        // договора по дням
  const [overallData, setOverallData] = useState([]);        // КП по дням

  // Доп. отчёты
  const [clientsToday, setClientsToday] = useState(0);
  const [clientsMonth, setClientsMonth] = useState(0);
  const [averageAge, setAverageAge] = useState(0);
  const [averageCheck, setAverageCheck] = useState(0);
  const [genderDistribution, setGenderDistribution] = useState([]);
  const [geographyDistribution, setGeographyDistribution] = useState([]);
  const [bestSellingCategories, setBestSellingCategories] = useState([]);
  const [selectedCategoryKey, setSelectedCategoryKey] = useState(null);
  const [dashboardWarnings, setDashboardWarnings] = useState([]);
  const [categorySearch, setCategorySearch] = useState("");
  const [isCategoryModalOpen, setCategoryModalOpen] = useState(false);

  // Подкатегории для дверей
  const [doorSubcategoryType, setDoorSubcategoryType] = useState("all"); // "all", "opening", "color"
  const [doorSubcategoryValue, setDoorSubcategoryValue] = useState(null);

  // Менеджеры
  const [managers, setManagers] = useState([]);
  const [pieMode, setPieMode] = useState("agreements");

  // Колонки для таблицы менеджеров
  const managerColumns = [
    { title: "Менеджер", dataIndex: "manager", key: "manager" },
    { title: "Договоры", dataIndex: "agreements", key: "agreements" },
    { title: "КП", dataIndex: "kp", key: "kp" },
  ];

  // Колонки для таблицы самых продаваемых товаров
  const productColumns = [
    {
      title: "Место",
      dataIndex: "rank",
      key: "rank",
      align: "center",
      width: 90,
      render: (rank) => <span className="rank-badge">{rank}</span>,
    },
    { title: "Товар", dataIndex: "name", key: "name" },
    {
      title: "Продано",
      dataIndex: "count",
      key: "count",
      align: "right",
      render: (value) => (value ?? 0).toLocaleString("ru-RU"),
    },
    {
      title: "Доля",
      dataIndex: "share",
      key: "share",
      align: "right",
      render: (value) => <span className="share-chip">{value}%</span>,
    },
  ];

  // Вызываем бекенд при смене периода
  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const start = dateRange[0].format("YYYY-MM-DD");
        const end = dateRange[1].format("YYYY-MM-DD");
        const data = await fetchDashboardData({ start, end });

        // Заливаем полученные данные
        setRequestsData(data.requestsData || []);
        setManagerData(data.managerData || []);
        setOverallData(data.overallData || []);
        setClientsToday(data.clientsToday || 0);
        setClientsMonth(data.clientsMonth || 0);
        setAverageAge(data.averageAge || 0);
        setAverageCheck(data.averageCheck || 0);
        setGenderDistribution(data.genderDistribution || []);
        setGeographyDistribution(data.geographyDistribution || []);

        const categories = Array.isArray(data.bestSelling)
          ? data.bestSelling
          : [];
        setBestSellingCategories(categories);
        setSelectedCategoryKey((prev) => {
          if (categories.length === 0) {
            return null;
          }
          const hasPrev =
            prev && categories.some((category) => category.category === prev);
          return hasPrev ? prev : categories[0].category;
        });

        const warnings = Array.isArray(data.errors) ? data.errors : [];
        setDashboardWarnings(warnings);
        warnings.forEach((warn) => {
          if (warn) {
            message.warning(warn);
          }
        });

        // Менеджеры
        const transformed = transformManagers(data.managers || []);
        setManagers(transformed);
      } catch (err) {
        console.error("Dashboard fetch error:", err);
        message.error("Не удалось загрузить статистику");
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [dateRange]);

  // Обработчики даты
  const onDateChange = (dates) => setDateRange(dates);
  const setQuickRange = (type) => {
    switch (type) {
      case "today":
        return setDateRange([moment(), moment()]);
      case "yesterday":
        return setDateRange([
          moment().subtract(1, "days"),
          moment().subtract(1, "days"),
        ]);
      case "7days":
        return setDateRange([moment().subtract(7, "days"), moment()]);
      case "30days":
        return setDateRange([moment().subtract(30, "days"), moment()]);
      case "month":
        return setDateRange([
          moment().startOf("month"),
          moment().endOf("month"),
        ]);
      default:
        return;
    };
  };

  // Определяем тип выбранного периода
  const getPeriodType = () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return "other";

    const startStr = dateRange[0].format("YYYY-MM-DD");
    const endStr = dateRange[1].format("YYYY-MM-DD");

    const todayStr = moment().format("YYYY-MM-DD");
    const yesterdayStr = moment().subtract(1, "days").format("YYYY-MM-DD");
    const weekAgoStr = moment().subtract(7, "days").format("YYYY-MM-DD");
    const monthStartStr = moment().startOf("month").format("YYYY-MM-DD");
    const monthEndStr = moment().endOf("month").format("YYYY-MM-DD");

    // Проверяем "Сегодня"
    if (startStr === todayStr && endStr === todayStr) {
      return "today";
    }

    // Проверяем "Вчера"
    if (startStr === yesterdayStr && endStr === yesterdayStr) {
      return "yesterday";
    }

    // Проверяем "Неделя" (7 дней назад до сегодня)
    if (startStr === weekAgoStr && endStr === todayStr) {
      return "week";
    }

    // Проверяем "Месяц" (начало месяца до конца месяца)
    if (startStr === monthStartStr && endStr === monthEndStr) {
      return "month";
    }

    return "other";
  };

  const periodType = getPeriodType();

  // Формируем строку периода для отображения
  const periodLabel = dateRange && dateRange[0] && dateRange[1]
    ? `${dateRange[0].format("DD.MM.YYYY")} - ${dateRange[1].format("DD.MM.YYYY")}`
    : "";

  // Вычисляемые данные для круговых диаграмм
  const totalClients = requestsData.reduce((s, { value }) => s + value, 0);
  const totalAgreements = managerData.reduce((s, { value }) => s + value, 0);
  const totalProposals = overallData.reduce((s, { value }) => s + value, 0);
  const pieDistributionData = [
    { name: "Клиенты", value: totalClients },
    { name: "Договоры", value: totalAgreements },
    { name: "КП", value: totalProposals },
  ];

  // Данные для PieChart менеджеров
  const bottomPieData = managers.map((m) => ({
    name: m.manager,
    value: pieMode === "agreements" ? m.agreements : m.kp,
  }));

  const openCategoryModal = () => {
    setCategorySearch("");
    setCategoryModalOpen(true);
  };

  const closeCategoryModal = () => {
    setCategoryModalOpen(false);
    setCategorySearch("");
  };

  const handleCategorySelect = (categoryName) => {
    setSelectedCategoryKey(categoryName);
    // Сбрасываем фильтры подкатегорий при смене категории
    setDoorSubcategoryType("all");
    setDoorSubcategoryValue(null);
    closeCategoryModal();
  };

  const normalizedCategorySearch = categorySearch.trim().toLowerCase();
  const filteredCategories =
    normalizedCategorySearch.length > 0
      ? bestSellingCategories.filter((category) =>
        category.category.toLowerCase().includes(normalizedCategorySearch)
      )
      : bestSellingCategories;

  const selectedCategory = bestSellingCategories.find(
    (category) => category.category === selectedCategoryKey
  );

  // Проверяем, является ли выбранная категория "Двери"
  const isDoorCategory = selectedCategoryKey?.toLowerCase().includes("двери") ||
    selectedCategoryKey?.toLowerCase().includes("дверь");

  // Фиксированные значения подкатегорий для дверей
  const doorSubcategories = useMemo(() => {
    return {
      openingTypes: [
        "Наружное правое",
        "Наружное левое",
        "Внутреннее правое",
        "Внутреннее левое",
        "Слева направо",
        "Справа налево",
        "Без открывания"
      ],
      colors: [
        { value: "SILVER", label: "Серебро" },
        { value: "BLACK", label: "Чёрный" },
        { value: "GOLD", label: "Золото" },
        { value: "WITHOUT_ALUMINIUM", label: "Без алюминия" }
      ]
    };
  }, []);

  const selectedCategoryTotal =
    selectedCategory?.totalCount ??
    selectedCategory?.items?.reduce((sum, item) => sum + (item.count || 0), 0) ??
    0;

  // Фильтруем товары по подкатегориям для дверей
  const filteredItems = useMemo(() => {
    if (!selectedCategory?.items) return [];

    let items = [...selectedCategory.items];

    // Применяем фильтр подкатегории для дверей
    if (isDoorCategory && doorSubcategoryType !== "all" && doorSubcategoryValue) {
      if (doorSubcategoryType === "opening") {
        items = items.filter(item => item.openingType === doorSubcategoryValue);
      } else if (doorSubcategoryType === "color") {
        items = items.filter(item => item.color === doorSubcategoryValue);
      }
    }

    return items;
  }, [selectedCategory, isDoorCategory, doorSubcategoryType, doorSubcategoryValue]);

  // Вычисляем итог для отфильтрованных товаров
  const filteredTotal = filteredItems.reduce((sum, item) => sum + (item.count || 0), 0);

  const selectedCategoryItems = filteredItems.map((item, idx) => ({
    ...item,
    key: item.productId || `${item.name}-${idx}`,
    rank: idx + 1,
    share:
      filteredTotal > 0
        ? Math.round((item.count / filteredTotal) * 100)
        : 0,
  }));

  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Выбор периода */}
      <div className="dashboard-header">
        <div className="dashboard-title-section">
          <h1 className="dashboard-title">
            <RiseOutlined style={{ marginRight: 12 }} />
            Аналитическая панель
          </h1>
          <p className="dashboard-subtitle">
            Обзор ключевых показателей эффективности
          </p>
        </div>
        <div className="dashboard-controls">
          <Space size="middle" wrap>
            <RangePicker
              value={dateRange}
              onChange={onDateChange}
              format="DD MMM, YYYY"
              suffixIcon={<CalendarOutlined />}
              className="custom-range-picker"
            />
            <Button
              className="quick-range-btn"
              onClick={() => setQuickRange("today")}
            >
              Сегодня
            </Button>
            <Button
              className="quick-range-btn"
              onClick={() => setQuickRange("yesterday")}
            >
              Вчера
            </Button>
            <Button
              className="quick-range-btn"
              onClick={() => setQuickRange("7days")}
            >
              Неделя
            </Button>
            <Button
              className="quick-range-btn"
              type="primary"
              onClick={() => setQuickRange("month")}
            >
              Месяц
            </Button>
          </Space>
        </div>
      </div>

      {dashboardWarnings.length > 0 ? (
        <div className="dashboard-warnings">
          {dashboardWarnings.map((warning, idx) => (
            <div className="warning-pill" key={`${warning}-${idx}`}>
              {warning}
            </div>
          ))}
        </div>
      ) : null}

      {/* Карточки основных KPI */}
      <Row gutter={[24, 24]} className="kpi-row">
        {/* Первая карточка клиентов - зависит от периода */}
        <Col xs={24} sm={12} lg={6}>
          <div className="kpi-card kpi-card-purple">
            <div className="kpi-icon">
              <UserOutlined />
            </div>
            <div className="kpi-content">
              <div className="kpi-label">
                {periodType === "today" && "Клиентов за сегодня"}
                {periodType === "yesterday" && "Клиентов за вчера"}
                {periodType === "week" && "Клиентов за неделю"}
                {periodType === "month" && "Клиентов за месяц"}
                {periodType === "other" && `Клиентов за период`}
              </div>
              <div className="kpi-value">
                {periodType === "today" && clientsToday}
                {periodType === "month" && clientsMonth}
                {(periodType === "yesterday" || periodType === "week" || periodType === "other") && totalClients}
              </div>
              {periodType === "other" && (
                <div className="kpi-period" style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>
                  {periodLabel}
                </div>
              )}
            </div>
          </div>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <div className="kpi-card kpi-card-blue">
            <div className="kpi-icon">
              <TrophyOutlined />
            </div>
            <div className="kpi-content">
              <div className="kpi-label">Средний возраст</div>
              <div className="kpi-value">{Math.round(averageAge)} лет</div>
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div className="kpi-card kpi-card-green">
            <div className="kpi-icon">
              <DollarOutlined />
            </div>
            <div className="kpi-content">
              <div className="kpi-label">Средний чек</div>
              <div className="kpi-value">{Math.round(averageCheck)} тг</div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Тренды (AreaChart) */}
      <Row gutter={[24, 24]} style={{ marginTop: 32 }}>
        <Col xs={24} lg={8}>
          <Card className="chart-card" title="Количество клиентов" bordered={false}>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={requestsData}>
                <defs>
                  <linearGradient id="colorClients" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1890ff" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#1890ff" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  stroke="#8884d8"
                  style={{ fontSize: 12 }}
                />
                <YAxis allowDecimals={false} stroke="#8884d8" style={{ fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    borderRadius: 8,
                    border: "none",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#1890ff"
                  strokeWidth={3}
                  fill="url(#colorClients)"
                  name="Клиенты"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card className="chart-card" title="Количество договоров" bordered={false}>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={managerData}>
                <defs>
                  <linearGradient id="colorAgreements" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f093fb" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#f093fb" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  stroke="#8884d8"
                  style={{ fontSize: 12 }}
                />
                <YAxis allowDecimals={false} stroke="#8884d8" style={{ fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    borderRadius: 8,
                    border: "none",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  }}
                />
                {managerData.length > 0 ? (
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#f093fb"
                    strokeWidth={3}
                    fill="url(#colorAgreements)"
                    name="Договоры"
                  />
                ) : null}
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card className="chart-card" title="Количество КП" bordered={false}>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={overallData}>
                <defs>
                  <linearGradient id="colorProposals" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4facfe" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#4facfe" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  stroke="#8884d8"
                  style={{ fontSize: 12 }}
                />
                <YAxis allowDecimals={false} stroke="#8884d8" style={{ fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    borderRadius: 8,
                    border: "none",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  }}
                />
                {overallData.length > 0 ? (
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#4facfe"
                    strokeWidth={3}
                    fill="url(#colorProposals)"
                    name="КП"
                  />
                ) : null}
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Круговые и столбчатые диаграммы */}
      <Row gutter={[24, 24]} style={{ marginTop: 32 }}>
        <Col xs={24} lg={12}>
          <Card className="chart-card" title="Общее распределение" bordered={false}>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={pieDistributionData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  label={(entry) => entry.name}
                  labelLine={true}
                >
                  {pieDistributionData.map((entry, idx) => (
                    <Cell
                      key={idx}
                      fill={pieColors[idx % pieColors.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    borderRadius: 8,
                    border: "none",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card className="chart-card" title="Распределение по полу" bordered={false}>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={genderDistribution}
                  dataKey="count"
                  nameKey="gender"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  label={(entry) => entry.gender}
                  labelLine={true}
                >
                  {genderDistribution.map((entry, idx) => (
                    <Cell
                      key={idx}
                      fill={pieColors[idx % pieColors.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    borderRadius: 8,
                    border: "none",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* География клиентов */}
      <Row gutter={[24, 24]} style={{ marginTop: 32 }}>
        <Col xs={24}>
          <Card className="chart-card" title="География клиентов" bordered={false}>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={geographyDistribution}>
                <defs>
                  <linearGradient id="colorBar" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#43e97b" stopOpacity={0.9} />
                    <stop offset="95%" stopColor="#38f9d7" stopOpacity={0.7} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="city" stroke="#8884d8" style={{ fontSize: 12 }} />
                <YAxis stroke="#8884d8" style={{ fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    borderRadius: 8,
                    border: "none",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  }}
                />
                <Bar
                  dataKey="count"
                  fill="url(#colorBar)"
                  name="Количество"
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Самые продаваемые товары и менеджеры */}
      <Row gutter={[24, 24]} style={{ marginTop: 32, marginBottom: 32 }}>
        <Col xs={24} lg={12}>
          <Card className="chart-card table-card" title="Самые продаваемые товары" bordered={false}>
            {bestSellingCategories.length > 0 ? (
              <>
                <div className="category-selector">
                  <Button
                    className="category-select-btn"
                    icon={<AppstoreOutlined />}
                    onClick={openCategoryModal}
                    size="large"
                  >
                    {selectedCategoryKey || "Выберите категорию"}
                  </Button>
                  <span className="category-selector-count">
                    Категорий: {bestSellingCategories.length}
                  </span>
                </div>
                {selectedCategory ? (
                  <div className="category-summary">
                    <div className="category-summary-header">
                      <span className="category-title">
                        {selectedCategory.category}
                      </span>
                      <Tag className="category-total-tag">
                        Продано:{" "}
                        {(isDoorCategory && doorSubcategoryType !== "all"
                          ? filteredTotal
                          : selectedCategoryTotal
                        ).toLocaleString("ru-RU")}
                      </Tag>
                    </div>
                  </div>
                ) : null}

                {/* Подкатегории для дверей */}
                {isDoorCategory && (
                  <div className="door-subcategories" style={{ marginBottom: 16 }}>
                    <Tabs
                      activeKey={doorSubcategoryType}
                      onChange={(key) => {
                        setDoorSubcategoryType(key);
                        setDoorSubcategoryValue(null);
                      }}
                      size="small"
                    >
                      <Tabs.TabPane tab="Все двери" key="all" />
                      <Tabs.TabPane tab="По открыванию" key="opening" />
                      <Tabs.TabPane tab="По цвету" key="color" />
                    </Tabs>

                    {doorSubcategoryType === "opening" && (
                      <div style={{ marginTop: 8 }}>
                        <Radio.Group
                          value={doorSubcategoryValue}
                          onChange={(e) => setDoorSubcategoryValue(e.target.value)}
                          buttonStyle="solid"
                          size="small"
                        >
                          {doorSubcategories.openingTypes.map((type) => (
                            <Radio.Button key={type} value={type}>
                              {type}
                            </Radio.Button>
                          ))}
                        </Radio.Group>
                      </div>
                    )}

                    {doorSubcategoryType === "color" && (
                      <div style={{ marginTop: 8 }}>
                        <Radio.Group
                          value={doorSubcategoryValue}
                          onChange={(e) => setDoorSubcategoryValue(e.target.value)}
                          buttonStyle="solid"
                          size="small"
                        >
                          {doorSubcategories.colors.map((colorObj) => (
                            <Radio.Button key={colorObj.value} value={colorObj.value}>
                              {colorObj.label}
                            </Radio.Button>
                          ))}
                        </Radio.Group>
                      </div>
                    )}
                  </div>
                )}

                <Table
                  columns={productColumns}
                  dataSource={selectedCategoryItems}
                  pagination={false}
                  rowKey="key"
                  locale={{ emptyText: "Нет данных" }}
                  className="modern-table"
                />
              </>
            ) : (
              <div className="empty-state">
                <p>За выбранный период нет данных о продажах.</p>
              </div>
            )}
            <Modal
              visible={isCategoryModalOpen}
              onCancel={closeCategoryModal}
              footer={null}
              title="Выбор категории"
              centered
              className="category-modal"
            >
              {bestSellingCategories.length > 0 ? (
                <>
                  <Input
                    allowClear
                    placeholder="Поиск категории"
                    prefix={<SearchOutlined />}
                    value={categorySearch}
                    onChange={(event) => setCategorySearch(event.target.value)}
                    className="category-search-input"
                  />
                  <div className="category-modal-list">
                    {filteredCategories.length > 0 ? (
                      filteredCategories.map((category) => {
                        const total =
                          category.totalCount ??
                          category.items.reduce(
                            (sum, item) => sum + (item.count || 0),
                            0
                          );
                        const isActive =
                          selectedCategoryKey === category.category;

                        return (
                          <div
                            key={category.category}
                            className={`category-modal-item${isActive ? " active" : ""
                              }`}
                            onClick={() =>
                              handleCategorySelect(category.category)
                            }
                            onKeyDown={(event) => {
                              if (
                                event.key === "Enter" ||
                                event.key === " "
                              ) {
                                event.preventDefault();
                                handleCategorySelect(category.category);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                          >
                            <div className="category-modal-name">
                              {category.category}
                            </div>
                            <div className="category-modal-count">
                              {total.toLocaleString("ru-RU")} шт.
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <Empty
                        description="Категория не найдена"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        className="category-modal-empty"
                      />
                    )}
                  </div>
                </>
              ) : (
                <Empty
                  description="Категории отсутствуют"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  className="category-modal-empty"
                />
              )}
            </Modal>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            className="chart-card table-card"
            title="Заявки по менеджерам"
            bordered={false}
          >
            <Table
              columns={managerColumns}
              dataSource={managers}
              pagination={false}
              rowKey="manager"
              className="modern-table"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
